import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { LandingPage } from './features/marketing';
import { EmailVerificationPage as EmailVerification, LoginPage as Login, PasswordResetPage as PasswordReset, RegisterPage as Register } from './features/auth';
import { CoachDashboardPage as CoachDashboard, CoachPage, CoachTrainingPlansPage, CoachTrainingsPage } from './features/coach';
import { ProfilePage as Profile } from './features/profile';
import { AthleteDashboardPage as AthleteDashboard, AthleteTrainingPage } from './features/athlete';
import Logout from './components/Logout';
import { getAuthToken, getUserIdFromCookie } from './lib/auth';
import Coaches from './components/coach/Coaches';
import PrivacyPolicy from './components/PrivacyPolicy';
import ShareLinkHandler from './components/ShareLinkHandler';
import { HOST } from './config/api';
import { AppShell, PublicNavbar } from './shared/layout';
import { coachDemoData, coachDemoUser, athleteDemoData, athleteDemoUser } from './components/dashboard/demoData';

class AppErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = { hasError: false };
    }

    static getDerivedStateFromError() {
        return { hasError: true };
    }

    componentDidCatch(error) {
        console.error('Unhandled app error', error);
    }

    render() {
        if (this.state.hasError) {
            return (
                <div style={{ padding: '24px', color: '#30415f' }}>
                    Something went wrong while loading this screen. Please refresh and try again.
                </div>
            );
        }

        return this.props.children;
    }
}

function App() {
    const [userId, setUserId] = useState(getUserIdFromCookie());
    const [userRole, setUserRole] = useState(null);
    const [userInfo, setUserInfo] = useState(undefined);
    const [selectedVariant, setSelectedVariant] = useState(undefined);
    const [userSubscriptionsUpdated, setUserSubscriptionsUpdated] = useState(false);
    const [isBootstrapping, setIsBootstrapping] = useState(Boolean(userId));
    const [demoAthleteProfileInfo, setDemoAthleteProfileInfo] = useState(athleteDemoUser);
    const [demoCoachProfileInfo, setDemoCoachProfileInfo] = useState(coachDemoUser);

    useEffect(() => {
        let active = true;
        const controller = new AbortController();

        const fetchUserData = async () => {
            if (!userId) {
                setUserInfo(undefined);
                setUserRole(null);
                setIsBootstrapping(false);
                return;
            }

            setIsBootstrapping(true);

            try {
                const response = await fetch(`${HOST}/v1/profile?userId=${userId}`, {
                    method: 'GET',
                    signal: controller.signal,
                    headers: {
                        Authorization: `Bearer ${getAuthToken()}`,
                    },
                });

                if (response.status === 401) {
                    if (active) {
                        setUserId(null);
                        setUserRole(null);
                        setUserInfo(undefined);
                    }
                    return;
                }

                const data = await response.json();

                if (!active || !data?.user) {
                    return;
                }

                setUserInfo(data.user);
                setUserRole(data.user.userRole === 99 ? 1 : 2);
            } catch (error) {
                if (error.name === 'AbortError') {
                    return;
                }
                console.error('Failed to load user info', error);
            } finally {
                if (active) {
                    setIsBootstrapping(false);
                }
            }
        };

        fetchUserData();

        return () => {
            active = false;
            controller.abort();
        };
    }, [userId, userSubscriptionsUpdated]);

    useEffect(() => {
        setSelectedVariant((currentVariant) => {
            if (currentVariant !== undefined && currentVariant !== null) {
                return currentVariant;
            }

            return userInfo?.coachSubscription?.[0]?.variant_id ?? currentVariant;
        });
    }, [userInfo]);

    const renderPublic = (element) => (
        <>
            <PublicNavbar />
            {element}
        </>
    );

    const renderAppShell = (element) => (
        <AppShell
            userInfo={userInfo}
            userRole={userRole}
            setUserId={setUserId}
        >
            {element}
        </AppShell>
    );

    const renderHome = () => {
        if (!userId) {
            return renderPublic(<LandingPage />);
        }

        if (isBootstrapping || userRole === null || !userInfo) {
            return renderAppShell(<div style={{ padding: '24px', color: '#617397' }}>Loading workspace...</div>);
        }

        return userRole === 1
            ? renderAppShell(
                <CoachDashboard
                    userId={userId}
                    userInfo={userInfo}
                />
            )
            : renderAppShell(
                <AthleteDashboard
                    userId={userId}
                    userInfo={userInfo}
                />
            );
    };

    return (
        <AppErrorBoundary>
            <Router>
                <Routes>
                <Route path="/" element={renderHome()} />
                <Route path="/login" element={renderPublic(<Login setUserId={setUserId} setUserRole={setUserRole} />)} />
                <Route path="/register" element={renderPublic(<Register />)} />
                <Route path="/verify_email" element={renderPublic(<EmailVerification />)} />
                <Route path="/reset_password" element={renderPublic(<PasswordReset />)} />
                <Route path="/privacy_policy" element={renderPublic(<PrivacyPolicy setUserId={setUserId} />)} />
                <Route
                    path="/s/:code"
                    element={renderPublic(
                        <ShareLinkHandler
                            userId={userId}
                            userInfo={userInfo}
                            isBootstrapping={isBootstrapping}
                            setUserSubscriptionsUpdated={setUserSubscriptionsUpdated}
                        />
                    )}
                />
                <Route
                    path="/coaches"
                    element={
                        userId
                            ? renderAppShell(
                                <Coaches
                                    userInfo={userInfo}
                                    userSubscriptionsUpdated={userSubscriptionsUpdated}
                                    setUserSubscriptionsUpdated={setUserSubscriptionsUpdated}
                                    setSelectedVariant={setSelectedVariant}
                                />
                            )
                            : renderPublic(<Coaches userInfo={userInfo} />)
                    }
                />
                <Route
                    path="/trainings"
                    element={
                        userId && userRole === 1
                            ? renderAppShell(
                                <CoachPage showChecklist>
                                    <CoachTrainingsPage
                                        userId={userId}
                                        userInfo={userInfo}
                                    />
                                </CoachPage>
                            )
                            : userId && userRole === 2
                                ? renderAppShell(<AthleteTrainingPage userId={userId} />)
                                : renderPublic(<Login setUserId={setUserId} setUserRole={setUserRole} />)
                    }
                />
                <Route
                    path="/profile"
                    element={
                        userId
                            ? renderAppShell(
                                userRole === 1 ? (
                                    <Profile
                                        userId={userId}
                                        setUserId={setUserId}
                                        userInfo={userInfo}
                                        setUserInfo={setUserInfo}
                                        userRole={userRole}
                                        selectedVariant={selectedVariant}
                                        setSelectedVariant={setSelectedVariant}
                                    />
                                ) : (
                                    <Profile
                                        userId={userId}
                                        setUserId={setUserId}
                                        userInfo={userInfo}
                                        setUserInfo={setUserInfo}
                                        userRole={userRole}
                                        selectedVariant={selectedVariant}
                                        setSelectedVariant={setSelectedVariant}
                                    />
                                )
                            )
                            : renderPublic(<Login setUserId={setUserId} setUserRole={setUserRole} />)
                    }
                />
                <Route
                    path="/training_plans"
                    element={
                        userId && userRole === 1
                            ? renderAppShell(
                                <CoachPage showChecklist>
                                    <CoachTrainingPlansPage
                                        userId={userId}
                                        userInfo={userInfo}
                                    />
                                </CoachPage>
                            )
                            : userId
                                ? renderAppShell(<Profile
                                    userId={userId}
                                    setUserId={setUserId}
                                    userInfo={userInfo}
                                    setUserInfo={setUserInfo}
                                    userRole={userRole}
                                    selectedVariant={selectedVariant}
                                    setSelectedVariant={setSelectedVariant}
                                />)
                                : renderPublic(<Login setUserId={setUserId} setUserRole={setUserRole} />)
                    }
                />
                <Route path="/logout" element={<Logout setUserId={setUserId} />} />
                <Route
                    path="/demo/coach"
                    element={
                        <AppShell
                            userInfo={coachDemoUser}
                            userRole={1}
                            setUserId={setUserId}
                        >
                            <CoachDashboard userInfo={coachDemoUser} demoData={coachDemoData} />
                        </AppShell>
                    }
                />
                <Route
                    path="/demo/athlete"
                    element={
                        <AppShell
                            userInfo={athleteDemoUser}
                            userRole={2}
                            setUserId={setUserId}
                        >
                            <AthleteDashboard userInfo={athleteDemoUser} demoData={athleteDemoData} />
                        </AppShell>
                    }
                />
                <Route
                    path="/demo/athlete/profile"
                    element={
                        <AppShell
                            userInfo={demoAthleteProfileInfo}
                            userRole={2}
                            setUserId={setUserId}
                        >
                            <Profile
                                userId={0}
                                setUserId={setUserId}
                                userInfo={demoAthleteProfileInfo}
                                setUserInfo={setDemoAthleteProfileInfo}
                                userRole={2}
                                demoMode
                            />
                        </AppShell>
                    }
                />
                <Route
                    path="/demo/athlete/trainings"
                    element={
                        <AppShell
                            userInfo={athleteDemoUser}
                            userRole={2}
                            setUserId={setUserId}
                        >
                            <AthleteTrainingPage
                                userId={0}
                                demoMode
                                demoData={athleteDemoData}
                            />
                        </AppShell>
                    }
                />
                <Route
                    path="/demo/coach/profile"
                    element={
                        <AppShell
                            userInfo={demoCoachProfileInfo}
                            userRole={1}
                            setUserId={setUserId}
                        >
                            <Profile
                                userId={demoCoachProfileInfo.id}
                                setUserId={setUserId}
                                userInfo={demoCoachProfileInfo}
                                setUserInfo={setDemoCoachProfileInfo}
                                userRole={1}
                                demoMode
                            />
                        </AppShell>
                    }
                />
                <Route
                    path="/demo/coach/training_plans"
                    element={
                        <AppShell
                            userInfo={coachDemoUser}
                            userRole={1}
                            setUserId={setUserId}
                        >
                            <CoachPage showChecklist>
                                <CoachTrainingPlansPage
                                    userId={coachDemoUser.id}
                                    userInfo={coachDemoUser}
                                    demoMode
                                    demoData={coachDemoData}
                                />
                            </CoachPage>
                        </AppShell>
                    }
                />
                <Route
                    path="/demo/coach/trainings"
                    element={
                        <AppShell
                            userInfo={coachDemoUser}
                            userRole={1}
                            setUserId={setUserId}
                        >
                            <CoachPage showChecklist>
                                <CoachTrainingsPage
                                    userId={coachDemoUser.id}
                                    demoMode
                                    demoData={coachDemoData}
                                />
                            </CoachPage>
                        </AppShell>
                    }
                />
                </Routes>
            </Router>
        </AppErrorBoundary>
    );
}

export default App;
